/**
 * Canonical responsive breakpoints (docs/ui-design-tokens.md §2).
 *
 * These are the bucket STARTS (min-width values), not bucket endings:
 *
 *   xs  0          sm  480        md  768         lg  1024        xl  1280
 *   |-------------|-------------|--------------|--------------|------------>
 *   <480         480–767.98    768–1023.98    1024–1279.98    ≥1280
 *
 * `up(key)`  -> `(min-width: <bucket start>px)` — that bucket and larger.
 * `down(key)` -> `(max-width: <next bucket start - ε>px)` — the end of `key`'s
 *               bucket. `down('xl')` is `null` (xl has no upper bound).
 *
 * The epsilon (`0.02px`) is subtracted from the next bucket's start so an
 * `up()` boundary and its complement `down()` boundary never both match at the
 * exact boundary pixel (absorbs subpixel rounding).
 *
 * ADDITIVE foundation: these do NOT repoint the existing MUI
 * `theme.breakpoints` keys (still `{ 0, 320, 400, 480 }`). That migration is
 * deferred to a later consumer-migration package.
 */
export const BREAKPOINTS = {
    xs: 0,
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1280
};
/** Short alias for BREAKPOINTS. */
export const BP = BREAKPOINTS;
/** Epsilon subtracted from the next bucket start to separate up/down ranges. */
export const BREAKPOINT_EPSILON = 0.02;
const ORDER = ['xs', 'sm', 'md', 'lg', 'xl'];
/**
 * Min-width of a bucket start (that bucket and larger).
 *
 * @param {BreakpointKey} key - The bucket whose start to match at/above.
 * @returns {string} A CSS media feature, e.g. `'(min-width: 768px)'`.
 */
export function up(key) {
    return `(min-width: ${BREAKPOINTS[key]}px)`;
}
/**
 * Max-width immediately before the NEXT bucket (the end of `key`'s bucket).
 *
 * @param {BreakpointKey} key - The bucket whose end to match below.
 * @returns {string|null} A CSS media feature, e.g. `'(max-width: 767.98px)'`,
 * or `null` for `xl` (no upper bound).
 */
export function down(key) {
    const index = ORDER.indexOf(key);
    const next = ORDER[index + 1];
    if (next === undefined) {
        return null;
    }
    return `(max-width: ${(BREAKPOINTS[next] - BREAKPOINT_EPSILON).toFixed(2)}px)`;
}
